/**
 * Vorlage „Im Ofen backen“.
 *
 * Der Ofen ist heiß, die Anzeige zeigt die Temperatur. Im Fenster steht die
 * Form auf dem Rost: Der helle Teig geht auf, wölbt sich und bräunt. Eine
 * Uhr zeigt die Backzeit, am Ende erscheint ein Häkchen.
 */
import { clamp, ease, lerp, segment, wave } from '../geometry.ts';
import { roundedRectPath } from '../paths.ts';
import { compact, faded, group, line, rect } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { cake } from '../parts/food.ts';
import { HEATING_MODES, OVEN, OVEN_WINDOW, ovenParts } from '../parts/oven.ts';
import type { HeatingMode } from '../parts/oven.ts';
import { arrow, BADGE_RIGHT, doneMark, durationLabel, timerBadge } from '../parts/stage.ts';
import { cakeTin } from '../parts/vessels.ts';
import { minutesFromText } from '../inference.ts';
import { choice, defineTemplate, numberValue, textValue } from '../template.ts';

/** Rost und Form im Fenster. */
const RACK_Y = OVEN_WINDOW.y + OVEN_WINDOW.height * 0.7;
const TIN = { cx: OVEN_WINDOW.x + OVEN_WINDOW.width / 2, width: 66, height: 10 } as const;
const TIN_RY = TIN.width * 0.16;
const RIM_Y = RACK_Y - TIN.height - TIN_RY + 2;

/** Zeitplan. */
const TIME = {
  rise: [0.04, 0.66],
  brown: [0.3, 0.76],
  done: [0.78, 0.83],
  reset: [0.92, 1],
} as const;

/** Vorlage „Im Ofen backen“. */
export const bakeBakeTemplate = defineTemplate({
  id: 'bake.bake',
  group: 'bake',
  title: 'Im Ofen backen',
  description: 'Die Form steht im heißen Ofen. Der Teig geht auf und bräunt, bis die Backzeit um ist.',
  durationSeconds: 6,
  keywords: [
    'backen',
    'minuten backen',
    'im ofen',
    'im backofen',
    'goldbraun',
    'goldgelb',
    'backzeit',
    'überbacken',
  ],
  stillTime: 0.7,
  parameters: [
    {
      kind: 'number',
      name: 'temperature',
      label: 'Temperatur',
      min: 50,
      max: 250,
      step: 10,
      unit: '°C',
      defaultValue: 180,
    },
    choice('mode', 'Heizart', HEATING_MODES, 'topBottom'),
    {
      kind: 'number',
      name: 'minutes',
      label: 'Backzeit',
      min: 5,
      max: 240,
      step: 5,
      unit: 'Min',
      defaultValue: 40,
    },
  ],
  inferValues(text) {
    const lower = text.toLowerCase();
    const values: Record<string, string | number | undefined> = { minutes: minutesFromText(text) };
    const match = /(\d{2,3})\s*(?:°|grad)/.exec(lower);
    if (match?.[1] !== undefined) {
      values.temperature = Number(match[1]);
    }
    if (lower.includes('umluft')) values.mode = 'convection';
    else if (lower.includes('grill')) values.mode = 'grill';
    else if (lower.includes('unterhitze') && !lower.includes('ober')) values.mode = 'bottom';
    else if (lower.includes('ober')) values.mode = 'topBottom';
    return values;
  },
  scene(values, t) {
    const temperature = numberValue(values, 'temperature', 180);
    const minutes = numberValue(values, 'minutes', 40);
    const mode = textValue(values, 'mode', 'topBottom') as HeatingMode;
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const rise = ease.inOutSine(segment(t, ...TIME.rise)) * (1 - reset);
    const brown = ease.inOutSine(segment(t, ...TIME.brown)) * (1 - reset);
    const done = clamp(ease.outBack(segment(t, ...TIME.done)) * 1.2) * (1 - reset);
    const { body, door } = ovenParts({ temperature, mode, heating: true, knobAngle: 135 });

    const tin = cakeTin(TIN.cx, RIM_Y, TIN.width, TIN.height);
    const width = TIN.width - 4;
    const height = lerp(1, 9, rise);
    const dome = lerp(0, 6, rise);
    const top = mode === 'topBottom' || mode === 'grill' || mode === 'convection';
    const bottom = mode === 'topBottom' || mode === 'bottom' || mode === 'convection';
    // Leichtes Flimmern der Glut, schleifenfest über ganze Umläufe.
    const glow = 0.4 + 0.1 * wave(t, 3);
    const glowBand = (y: number): SceneNode =>
      rect(OVEN_WINDOW.x, y - 8, OVEN_WINDOW.width, 16, 0, { fill: 'accentTint', stroke: 'none' });
    const inside = group(
      compact([
        rect(OVEN_WINDOW.x, OVEN_WINDOW.y, OVEN_WINDOW.width, OVEN_WINDOW.height, 0, {
          fill: 'cavity',
          stroke: 'none',
        }),
        faded(glow, [
          top ? glowBand(OVEN_WINDOW.y + 6) : null,
          bottom ? glowBand(OVEN_WINDOW.y + OVEN_WINDOW.height - 6) : null,
        ]),
        line(OVEN_WINDOW.x + 4, RACK_Y, OVEN_WINDOW.x + OVEN_WINDOW.width - 4, RACK_Y, {
          stroke: 'deep',
          strokeWidth: 2,
        }),
        ...tin.back,
        // Roher Teig ist hell; die Kruste blendet beim Bräunen darüber ein.
        ...cake(TIN.cx, RIM_Y + 4, width, height, dome, 'paper'),
        faded(brown, cake(TIN.cx, RIM_Y + 4, width, height, dome)),
        ...tin.front,
      ]),
      {
        clip: roundedRectPath(OVEN_WINDOW.x, OVEN_WINDOW.y, OVEN_WINDOW.width, OVEN_WINDOW.height, 7),
      },
    );

    return compact([
      ...body,
      inside,
      ...door,
      timerBadge(BADGE_RIGHT, 13, durationLabel(minutes), t),
      faded(done, [doneMark(OVEN.x + OVEN.width + 19, OVEN.y + OVEN.panel + 30, 1.2)]),
    ]);
  },
  hints() {
    return [
      arrow(
        [
          { x: TIN.cx - 46, y: RIM_Y + 4 },
          { x: TIN.cx - 46, y: RIM_Y - 14 },
        ],
        'accent',
        7,
      ),
    ];
  },
});
